/**
 * Standalone IIFE Entry Point
 * Exposes Atmosphere API to window object for script tag usage
 * Includes UI, Benchmark, Analytics, and Smart Mute features
 * 
 * Execution Flow:
 * 1. Immediate mute check on script load
 * 2. Run benchmark to determine device capability
 * 3. Block 'Low' performance devices
 * 4. Inject UI (FAB only) for 'Mid'/'High' devices
 * 5. First click initializes engine, subsequent clicks toggle panel
 */

import { Engine, EngineOptions } from '../../core/Engine';
import { StateManager, DEFAULT_CONFIG } from '../../core/StateManager';
import type { AtmosphereConfig } from '../../core/StateManager';
import { SnowEffect } from '../../effects/SnowEffect';
import { Benchmark, benchmark } from '../../core/Benchmark';
import type { PerformanceLevel, BenchmarkResult } from '../../core/Benchmark';
import { Analytics, analytics } from '../../core/Analytics';
import { UIController, MuteDuration } from './UI/UIController';
import { SNOW_COLORS } from '../../core/Constants';
import type { SnowColor } from '../../core/Constants';

// Storage key for mute functionality
const MUTE_STORAGE_KEY = 'atmosphere_mute_until';

/**
 * Check if the snow effect is currently muted
 */
function isMuted(): boolean {
  try {
    const muteUntil = localStorage.getItem(MUTE_STORAGE_KEY);
    if (!muteUntil) return false;

    if (muteUntil === 'forever') return true;

    const muteTimestamp = parseInt(muteUntil, 10);
    if (isNaN(muteTimestamp)) return false;

    return Date.now() < muteTimestamp;
  } catch {
    // localStorage may be blocked
    return false;
  }
}

/**
 * Set mute duration
 */
function setMute(duration: MuteDuration): void {
  try {
    let muteValue: string;
    const now = Date.now();

    switch (duration) {
      case 'day':
        muteValue = String(now + 24 * 60 * 60 * 1000);
        break;
      case '3days':
        muteValue = String(now + 3 * 24 * 60 * 60 * 1000);
        break;
      case 'forever':
        muteValue = 'forever';
        break;
      default:
        return;
    }

    localStorage.setItem(MUTE_STORAGE_KEY, muteValue);
  } catch {
    // localStorage may be blocked
    console.warn('[Atmosphere] Could not save mute preference');
  }
}

/**
 * Clear mute setting
 */
function clearMute(): void {
  try {
    localStorage.removeItem(MUTE_STORAGE_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Main Atmosphere class for standalone usage
 * Supports a "prepared" state where UI is injected but Engine is not yet created
 */
class Atmosphere {
  private engine: Engine | null = null;
  private snowEffect: SnowEffect | null = null;
  private uiController: UIController | null = null;
  private benchmarkResult: BenchmarkResult | null = null;
  private benchmarkCode: string = '';  // 'H', 'M', or 'L'
  private isPrepared: boolean = false;
  private isEngineInitialized: boolean = false;

  /**
   * Prepare Atmosphere: inject UI but don't initialize engine yet
   * This is called automatically on script load for qualifying devices
   */
  async prepare(): Promise<boolean> {
    // Check if muted
    if (isMuted()) {
      console.log('[Atmosphere] Snow is muted. Call clearMute() to unmute.');
      return false;
    }

    if (this.isPrepared) {
      console.warn('[Atmosphere] Already prepared.');
      return true;
    }

    // Run benchmark
    try {
      this.benchmarkResult = await benchmark.run();
      this.benchmarkCode = this.benchmarkResult.level.charAt(0);  // 'H', 'M', or 'L'
      console.log(`[Atmosphere] Benchmark: ${this.benchmarkResult.level} (score: ${this.benchmarkResult.score})`);
      
      // Track benchmark
      analytics.trackBenchmarkComplete(this.benchmarkResult.level, this.benchmarkResult.score);
    } catch (error) {
      console.warn('[Atmosphere] Benchmark failed:', error);
      // Default to Mid on failure
      this.benchmarkResult = { level: 'Mid', score: 50, executionTime: 250 };
      this.benchmarkCode = 'M';
    }

    // Performance guard - block Low devices
    if (this.benchmarkResult.level === 'Low') {
      console.warn('Atmosphere: BM unqualified. Exit.');
      return false;
    }

    // Inject UI (FAB only, no engine yet)
    this.initializeUI();
    this.isPrepared = true;

    console.log('🌨️ Atmosphere prepared (UI injected, awaiting activation)');
    return true;
  }

  /**
   * Initialize the engine and start snow effect
   * Called on first FAB click
   */
  async activateEngine(options: EngineOptions = {}): Promise<void> {
    if (this.isEngineInitialized) {
      console.warn('[Atmosphere] Engine already initialized.');
      return;
    }

    if (!this.isPrepared) {
      // If not prepared, run prepare first
      const prepared = await this.prepare();
      if (!prepared) return;
    }

    // Create engine with benchmark-adjusted config
    const engineConfig = { ...options.config };
    if (this.benchmarkResult) {
      const profile = Benchmark.getConfigForLevel(this.benchmarkResult.level);
      engineConfig.particleCount = engineConfig.particleCount ?? profile.particleCount;
    }

    this.engine = new Engine({
      container: options.container,
      config: engineConfig,
    });
    
    // Create and register snow effect
    this.snowEffect = new SnowEffect();
    this.engine.registerEffect('snow', this.snowEffect);

    // Start the engine
    this.engine.start();
    this.isEngineInitialized = true;
    
    // Track snow start
    const config = this.engine.getConfig();
    analytics.trackSnowStart({
      intensity: config.snow.intensity,
      particleCount: config.particleCount,
    });

    // Update UI to reflect engine is active
    if (this.uiController) {
      this.uiController.setEngineActive(true);
      this.uiController.updateFromConfig(this.engine.getConfig());
    }

    console.log('🌨️ Atmosphere engine activated');
  }

  /**
   * Legacy init method - now calls prepare + activateEngine
   */
  async init(options: EngineOptions & { 
    showUI?: boolean;
    runBenchmark?: boolean;
    analyticsId?: string;
  } = {}): Promise<void> {
    // Initialize analytics if measurement ID provided
    if (options.analyticsId) {
      analytics.setEnabled(true);
      (analytics as unknown as { measurementId: string }).measurementId = options.analyticsId;
      await analytics.initialize();
    }

    // Prepare and activate
    await this.prepare();
    await this.activateEngine(options);
  }

  /**
   * Initialize the UI controller
   */
  private initializeUI(): void {
    this.uiController = new UIController({
      benchmarkCode: this.benchmarkCode,
      onActivate: () => {
        // First click - activate engine
        this.activateEngine();
      },
      onIntensityChange: (intensity) => {
        this.setIntensity(intensity);
        analytics.trackConfigChanged('intensity', intensity);
      },
      onWindChange: (strength) => {
        // Directional wind: -5.0 (left) to 5.0 (right)
        this.setWind(strength);
        analytics.trackConfigChanged('wind', strength);
      },
      onColorChange: (color) => {
        this.setColor(color.r, color.g, color.b);
        analytics.trackConfigChanged('color', color.name);
      },
      onAccumulationToggle: (enabled) => {
        this.setAccumulation(enabled);
        analytics.trackConfigChanged('accumulation', enabled);
      },
      onMute: (duration) => {
        setMute(duration);
        analytics.trackMuteClicked(duration);
        // Fade out gracefully before destroying
        this.fadeOutAndDestroy();
        console.log(`[Atmosphere] Muted for ${duration}`);
      },
      onToggle: (enabled) => {
        this.setEnabled(enabled);
        if (enabled) {
          analytics.trackSnowStart();
        } else {
          analytics.trackSnowStop();
        }
      },
    });

    this.uiController.inject();
  }

  /**
   * Start the effect (if stopped)
   */
  start(): void {
    if (isMuted()) {
      console.log('[Atmosphere] Cannot start - snow is muted');
      return;
    }

    if (this.engine) {
      this.engine.start();
      analytics.trackSnowStart();
    } else if (this.isPrepared) {
      this.activateEngine();
    } else {
      this.prepare().then(() => this.activateEngine());
    }
  }

  /**
   * Stop the effect temporarily
   */
  stop(): void {
    if (this.engine) {
      this.engine.stop();
      analytics.trackSnowStop();
    }
  }

  /**
   * Destroy the instance and clean up resources
   */
  destroy(): void {
    if (this.uiController) {
      this.uiController.remove();
      this.uiController = null;
    }

    if (this.engine) {
      this.engine.stop();
      this.engine = null;
      this.snowEffect = null;
      console.log('🌨️ Atmosphere destroyed');
    }

    this.isPrepared = false;
    this.isEngineInitialized = false;
  }

  /**
   * Fade out the snow effect gracefully, then destroy
   * Creates a "melting away" visual effect
   */
  fadeOutAndDestroy(duration: number = 1500): void {
    if (!this.snowEffect) {
      this.destroy();
      return;
    }

    // Remove UI immediately so user knows action was received
    if (this.uiController) {
      this.uiController.remove();
      this.uiController = null;
    }

    // Start the fade animation
    this.snowEffect.fadeOut(duration, () => {
      // Destroy after fade completes
      this.destroy();
    });
  }

  /**
   * Update configuration
   */
  updateConfig(updates: Partial<AtmosphereConfig>): void {
    if (this.engine) {
      this.engine.updateConfig(updates);
      this.uiController?.updateFromConfig(this.engine.getConfig());
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): Readonly<AtmosphereConfig> | null {
    return this.engine?.getConfig() || null;
  }

  /**
   * Set snow intensity (0-1)
   */
  setIntensity(intensity: number): void {
    this.updateConfig({
      snow: {
        ...DEFAULT_CONFIG.snow,
        ...this.engine?.getConfig().snow,
        intensity: Math.max(0, Math.min(1, intensity)),
      },
    });
  }

  /**
   * Enable or disable snow effect
   */
  setEnabled(enabled: boolean): void {
    this.updateConfig({ enabled });
  }

  /**
   * Set wind parameters
   * @param strength - Directional wind strength: -5.0 (left) to 5.0 (right), 0 = no wind
   * @param enabled - Whether wind is enabled
   */
  setWind(strength: number, enabled: boolean = true): void {
    const currentSnow = this.engine?.getConfig().snow ?? DEFAULT_CONFIG.snow;
    this.updateConfig({
      snow: {
        ...currentSnow,
        wind: {
          ...currentSnow.wind,
          enabled,
          strength: Math.max(-5, Math.min(5, strength)),  // Directional: -5 to 5
        },
      },
    });
  }

  /**
   * Set snow color
   * @param r - Red component (0-255)
   * @param g - Green component (0-255)
   * @param b - Blue component (0-255)
   * @param alpha - Alpha/opacity (0-1), defaults to 0.85
   */
  setColor(r: number, g: number, b: number, alpha: number = 0.85): void {
    const currentSnow = this.engine?.getConfig().snow ?? DEFAULT_CONFIG.snow;
    this.updateConfig({
      snow: {
        ...currentSnow,
        color: {
          r: Math.max(0, Math.min(255, r)),
          g: Math.max(0, Math.min(255, g)),
          b: Math.max(0, Math.min(255, b)),
          alpha: Math.max(0, Math.min(1, alpha)),
        },
      },
    });
  }

  /**
   * Enable or disable accumulation
   */
  setAccumulation(enabled: boolean, maxHeight?: number): void {
    const currentSnow = this.engine?.getConfig().snow ?? DEFAULT_CONFIG.snow;
    this.updateConfig({
      snow: {
        ...currentSnow,
        accumulation: {
          ...currentSnow.accumulation,
          enabled,
          ...(maxHeight !== undefined && { maxHeight }),
        },
      },
    });
  }

  /**
   * Check if running
   */
  isRunning(): boolean {
    return this.engine?.getIsRunning() || false;
  }

  /**
   * Check if muted
   */
  isMuted(): boolean {
    return isMuted();
  }

  /**
   * Clear mute setting
   */
  clearMute(): void {
    clearMute();
    console.log('[Atmosphere] Mute cleared. Call init() to start snow.');
  }

  /**
   * Get benchmark result
   */
  getBenchmarkResult(): BenchmarkResult | null {
    return this.benchmarkResult;
  }

  /**
   * Check if prepared (UI injected)
   */
  getIsPrepared(): boolean {
    return this.isPrepared;
  }

  /**
   * Check if engine is initialized
   */
  getIsEngineInitialized(): boolean {
    return this.isEngineInitialized;
  }

  /**
   * Show UI (if not already shown)
   */
  showUI(): void {
    if (!this.uiController) {
      this.initializeUI();
    }
  }

  /**
   * Hide UI
   */
  hideUI(): void {
    if (this.uiController) {
      this.uiController.remove();
      this.uiController = null;
    }
  }
}

// Create singleton instance
const atmosphere = new Atmosphere();

// Export for module usage
export { Atmosphere, Engine, StateManager, SnowEffect, Benchmark, Analytics, UIController, DEFAULT_CONFIG, SNOW_COLORS };
export type { AtmosphereConfig, MuteDuration, PerformanceLevel, BenchmarkResult, SnowColor };

// Expose to window for IIFE usage
// Use explicit assignment to window.Atmosphere (not relying on Vite's lib.name)
if (typeof window !== 'undefined') {
  (window as unknown as { Atmosphere: Atmosphere }).Atmosphere = atmosphere;
}

// Immediate Execution Flow (no DOMContentLoaded, no data-attribute checks)
// Runs as soon as the script loads
(async function immediateExecution() {
  // Step 1: Mute check
  if (isMuted()) {
    console.log('[Atmosphere] Muted. Exiting.');
    return;
  }

  // Step 2 & 3: Benchmark and performance guard are handled in prepare()
  // Step 4: Inject UI if qualified
  await atmosphere.prepare();
})();

export default atmosphere;
